/**
 * Enhanced BankID Authentication Hook
 * Provides a clean interface for BankID authentication with improved error handling
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

// BankID States - matching backend
export enum BankIDState {
  IDLE = 'idle',
  INITIALIZING = 'initializing',
  WAITING_FOR_USER = 'waiting_for_user',
  USER_SIGNING = 'user_signing',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
  TIMEOUT = 'timeout',
  CANCELLED = 'cancelled'
}

// API URLs
const API_BASE_URL = process.env.REACT_APP_ENV === 'production'
  ? 'https://us-central1-hyria-58020.cloudfunctions.net'
  : 'https://us-central1-hyria-dev.cloudfunctions.net';

const BANKID_SIGN_URL = `${API_BASE_URL}/bankidSignProxy`;
const BANKID_COLLECT_URL = `${API_BASE_URL}/bankidCollectProxy`;
const BANKID_AUTH_COMPLETE_URL = `${API_BASE_URL}/bankidAuthCompleteImproved`;
const BANKID_QR_REFRESH_URL = `${API_BASE_URL}/bankidQrRefresh`;
const VALIDATE_SESSION_URL = `${API_BASE_URL}/validateBankIDSession`;

interface BankIDResponse {
  orderRef: string;
  autoStartToken: string;
  qrImage?: string;
  deviceType?: 'mobile' | 'desktop';
}

interface AuthResult {
  success: boolean;
  uid?: string;
  customToken?: string;
  sessionToken?: string;
  sessionExpiresAt?: string;
  isNewUser?: boolean;
  requiresProfileCompletion?: boolean;
  user?: {
    firstName: string;
    lastName: string;
    fullName: string;
    role: string;
    profileComplete: boolean;
    associationId?: string;
  };
  error?: string;
  code?: string;
}

interface UseBankIDReturn {
  // State
  state: BankIDState;
  message: string;
  error: string | null;
  qrImageUrl: string | null;
  autoStartToken: string | null;

  // Actions
  initiateBankID: () => Promise<void>;
  cancelBankID: () => void;

  // Utilities
  isMobile: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * Custom hook for BankID authentication
 */
export function useBankID(): UseBankIDReturn {
  // State management
  const [state, setState] = useState<BankIDState>(BankIDState.IDLE);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [autoStartToken, setAutoStartToken] = useState<string | null>(null);
  const [orderRef, setOrderRef] = useState<string | null>(null);

  // Refs for intervals
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const qrRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Device detection
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Derived states
  const isLoading = [
    BankIDState.INITIALIZING,
    BankIDState.WAITING_FOR_USER,
    BankIDState.USER_SIGNING,
    BankIDState.PROCESSING
  ].includes(state);

  const isAuthenticated = state === BankIDState.SUCCESS;

  /**
   * Store session in secure storage
   */
  const storeSession = useCallback(async (sessionToken: string, uid: string, expiresAt: string) => {
    try {
      // Store in localStorage with expiry
      const sessionData = {
        token: sessionToken,
        uid,
        expiresAt,
        createdAt: new Date().toISOString()
      };

      localStorage.setItem('bankid_session', JSON.stringify(sessionData));

      // Also store session reference in Firestore for validation
      await setDoc(doc(db, 'sessions', uid), {
        lastActivity: new Date().toISOString(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        }
      }, { merge: true });

    } catch (error) {
    }
  }, []);

  /**
   * Check for existing valid session
   */
  const checkExistingSession = useCallback(async (): Promise<boolean> => {
    try {
      const sessionStr = localStorage.getItem('bankid_session');
      if (!sessionStr) return false;

      const session = JSON.parse(sessionStr);
      const expiresAt = new Date(session.expiresAt);

      // Check if session is expired
      if (expiresAt < new Date()) {
        localStorage.removeItem('bankid_session');
        return false;
      }

      // Validate session with backend
      const response = await fetch(VALIDATE_SESSION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: session.uid,
          sessionToken: session.token
        })
      });

      const data = await response.json();

      if (data.success && data.customToken) {
        // Re-authenticate with custom token
        await signInWithCustomToken(auth, data.customToken);
        setState(BankIDState.SUCCESS);
        setMessage('Inloggad med sparad session');
        return true;
      }

      // Invalid session, remove it
      localStorage.removeItem('bankid_session');
      return false;

    } catch (error) {
      localStorage.removeItem('bankid_session');
      return false;
    }
  }, []);

  /**
   * Clean up intervals
   */
  const cleanupIntervals = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (qrRefreshIntervalRef.current) {
      clearInterval(qrRefreshIntervalRef.current);
      qrRefreshIntervalRef.current = null;
    }
  }, []);

  /**
   * Handle successful authentication
   */
  const handleAuthSuccess = useCallback(async (orderRef: string) => {
    try {
      setState(BankIDState.PROCESSING);
      setMessage('Slutför autentisering...');

      // Servern verifierar ordern mot BankID och skapar sessionen
      const response = await fetch(BANKID_AUTH_COMPLETE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderRef })
      });

      const result: AuthResult = await response.json();

      if (result.success && result.customToken) {
        // Sign in with custom token
        await signInWithCustomToken(auth, result.customToken);

        // Store session if provided
        if (result.sessionToken && result.uid) {
          await storeSession(
            result.sessionToken,
            result.uid,
            result.sessionExpiresAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          );
        }

        // Always try to complete profile and match housing (for both new and existing users)
        // This ensures housing is matched/created even if user already exists
        await completeUserProfile(result.uid!);

        setState(BankIDState.SUCCESS);
        setMessage('Autentisering lyckades!');
        setError(null);

        // Navigate based on user role
        setTimeout(() => {
          const role = result.user?.role || 'user';
          if (role === 'hyria_admin') {
            window.location.href = '/hyria-dashboard';
          } else if (role === 'brfadmin') {
            window.location.href = '/dashboard';
          } else {
            window.location.href = '/';
          }
        }, 1000);

      } else {
        throw new Error(result.error || 'Authentication failed');
      }

    } catch (error) {
      setState(BankIDState.ERROR);
      setError(error instanceof Error ? error.message : 'Autentisering misslyckades');
      setMessage('');
    }
  }, [storeSession]);

  /**
   * Hämtar folkbokföringsadressen via servern och kopplar lägenhet.
   * Skriver aldrig associationId/housingId/addressVerified på profilen —
   * de fälten sätts bara av servern (Firestore-reglerna spärrar dem).
   */
  const completeUserProfile = async (uid: string) => {
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../config/firebase');
      const lookup = httpsCallable<Record<string, never>, { success: boolean; data?: any }>(functions, 'getMyRegisteredAddress');
      const result = await lookup({});
      const data = result.data;

      if (data.success && data.data?.Basic) {
        const { Street, ZipCode, City, ApartmentNumber } = data.data.Basic;

        await setDoc(doc(db, 'users', uid), {
          address: Street,
          zipCode: ZipCode,
          city: City,
          apartmentNumber: ApartmentNumber,
          profileUpdatedAt: new Date().toISOString()
        }, { merge: true });

        const {
          findMatchingHousing,
          updateHousingOwnership,
          saveAddressToHousing,
          checkUserHasVerifiedAddress
        } = await import('../api/housing');

        const profileSnap = await getDoc(doc(db, 'users', uid));
        const profile = profileSnap.data();
        const personalNumber = profile?.personalNumber || '';
        const userInfo = {
          email: profile?.email || '',
          firstName: profile?.firstName || '',
          lastName: profile?.lastName || '',
          uid
        };

        const existingCheck = await checkUserHasVerifiedAddress(uid, personalNumber);
        if (existingCheck.hasVerifiedAddress) {
          return;
        }

        const matchingHousing = await findMatchingHousing(data.data);
        if (matchingHousing) {
          await updateHousingOwnership(matchingHousing.id, userInfo, matchingHousing.data.associationId);
        } else {
          await saveAddressToHousing(data.data, userInfo, '', '');
        }
      }
    } catch (error) {
      // Icke-kritiskt: användaren kan koppla lägenhet senare under Mina lägenheter
    }
  };

  /**
   * Poll BankID status
   */
  const pollStatus = useCallback(async (orderRefValue: string) => {
    try {
      const response = await fetch(BANKID_COLLECT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderRef: orderRefValue })
      });

      const data = await response.json();

      if (data?.apiCallResponse?.Success) {
        const status = data.apiCallResponse.Response?.Status;
        const hintCode = data.apiCallResponse.Response?.HintCode;

        if (status === 'complete') {
          cleanupIntervals();
          await handleAuthSuccess(orderRefValue);
        } else if (status === 'failed') {
          cleanupIntervals();
          setState(BankIDState.ERROR);
          setError(getErrorMessage(hintCode));
          setMessage('');
        } else if (status === 'pending') {
          // Update state based on hint code
          const newState = mapHintCodeToState(hintCode);
          setState(newState);
          setMessage(getHintMessage(hintCode));
        }
      }
    } catch (error) {
      // Don't stop polling on network errors
    }
  }, [cleanupIntervals, handleAuthSuccess]);

  /**
   * Refresh QR code
   */
  const refreshQR = useCallback(async () => {
    if (!qrImageUrl) return;

    try {
      const response = await fetch(BANKID_QR_REFRESH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originalQrUrl: qrImageUrl })
      });

      const data = await response.json();

      if (data.success && data.qrImageUrl) {
        setQrImageUrl(data.qrImageUrl);
      }
    } catch (error) {
    }
  }, [qrImageUrl]);

  /**
   * Initiate BankID authentication
   */
  const initiateBankID = useCallback(async () => {
    try {
      // Clean up any existing intervals
      cleanupIntervals();

      // Reset state
      setState(BankIDState.INITIALIZING);
      setMessage('Startar BankID...');
      setError(null);
      setQrImageUrl(null);
      setAutoStartToken(null);
      setOrderRef(null);

      // Check for existing session first
      const hasSession = await checkExistingSession();
      if (hasSession) {
        return;
      }

      // Call BankID sign proxy
      const response = await fetch(BANKID_SIGN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (!data?.authResponse?.Success || !data?.apiCallResponse?.Success) {
        throw new Error(data?.authResponse?.ErrorMessage || 'BankID-förfrågan misslyckades');
      }

      const bankidData: BankIDResponse = {
        orderRef: data.apiCallResponse.Response.OrderRef,
        autoStartToken: data.apiCallResponse.Response.AutoStartToken,
        qrImage: data.apiCallResponse.Response.QrImage,
        deviceType: data.deviceType || (isMobile ? 'mobile' : 'desktop')
      };

      setOrderRef(bankidData.orderRef);
      setAutoStartToken(bankidData.autoStartToken);

      if (bankidData.qrImage) {
        setQrImageUrl(bankidData.qrImage);
      }

      setState(BankIDState.WAITING_FOR_USER);
      setMessage('Väntar på att du ska öppna BankID...');

      // Start polling (every 2 seconds)
      pollingIntervalRef.current = setInterval(() => {
        pollStatus(bankidData.orderRef);
      }, 2000);

      // Start QR refresh (every 5 seconds) if on desktop
      if (!isMobile && bankidData.qrImage) {
        qrRefreshIntervalRef.current = setInterval(() => {
          refreshQR();
        }, 5000);
      }

      // Samma enhet (mobil): appen öppnas av användarens tryck på "Öppna BankID"
      // i BankIDSignIn, inte genom ett automatiskt adressbyte här. Webbläsaren
      // låter en sida öppna en app bara några sekunder efter ett tryck; kommer
      // adressbytet senare (kalla funktioner) laddas BankID:s webbsida i stället
      // och den här sidan, som ska fråga efter status, försvinner.

    } catch (error) {
      setState(BankIDState.ERROR);
      setError(error instanceof Error ? error.message : 'Kunde inte starta BankID');
      setMessage('');
      cleanupIntervals();
    }
  }, [checkExistingSession, cleanupIntervals, isMobile, pollStatus, refreshQR]);

  /**
   * Cancel BankID authentication
   */
  const cancelBankID = useCallback(() => {
    cleanupIntervals();
    setState(BankIDState.IDLE);
    setMessage('');
    setError(null);
    setQrImageUrl(null);
    setAutoStartToken(null);
    setOrderRef(null);
  }, [cleanupIntervals]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupIntervals();
    };
  }, [cleanupIntervals]);

  return {
    state,
    message,
    error,
    qrImageUrl,
    autoStartToken,
    initiateBankID,
    cancelBankID,
    isMobile,
    isLoading,
    isAuthenticated
  };
}

// Helper functions
function mapHintCodeToState(hintCode: string): BankIDState {
  switch (hintCode) {
    case 'outstandingTransaction':
    case 'noClient':
      return BankIDState.WAITING_FOR_USER;
    case 'started':
    case 'userSign':
      return BankIDState.USER_SIGNING;
    default:
      return BankIDState.PROCESSING;
  }
}

function getHintMessage(hintCode: string): string {
  const messages: Record<string, string> = {
    'outstandingTransaction': 'Väntar på att du ska öppna BankID-appen...',
    'noClient': 'Starta BankID-appen på din telefon eller dator',
    'started': 'BankID-appen är öppnad, väntar på din legitimation...',
    'userSign': 'Bekräfta din identitet i BankID-appen...'
  };
  return messages[hintCode] || 'Väntar på BankID...';
}

function getErrorMessage(hintCode: string): string {
  const messages: Record<string, string> = {
    'expiredTransaction': 'BankID-sessionen har gått ut. Försök igen.',
    'certificateErr': 'Problem med ditt BankID-certifikat.',
    'userCancel': 'Du avbröt BankID-autentiseringen.',
    'cancelled': 'Autentiseringen avbröts.',
    'startFailed': 'Kunde inte starta BankID. Kontrollera att appen är installerad.'
  };
  return messages[hintCode] || 'Ett fel uppstod med BankID-autentiseringen.';
}